import { AxiosError, AxiosResponse } from "axios";
import _cloneDeepWith from "lodash/cloneDeepWith";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _isNil from "lodash/isNil";
import { env } from "next-runtime-env";

export const CLOUDINARY_CONFIGURATION_ERROR = new Error(
  "Missing Cloudinary configuration. Photos can't be uploaded while this error persists."
);

export const PRISMIC_CONFIGURATION_ERROR = new Error(
  "Missing Prismic configuration. Application can't continue without it."
);

export const SSR_CONFIGURATION_ERROR = new Error(
  "Server side data is missing. Make sure you've called server APIs in `getStaticProps` or `getServerSideProps`"
);

export const MISSING_CUSTOMISATION_ERROR = new Error(
  "Customisation data is missing. This should have been initialised through the URL, a cookie, or localStorage."
);

export const FAILED_SIGNATURE_ERROR = new Error(
  "Sorry, we weren't able to upload your photo. Please reload the page and try again."
);

export const MISSING_ESSENTIAL_DATA_ERROR = new Error(
  "Missing essential data to perform request. Please try again."
);

export function prismicMissingFieldError(key?: string) {
  return Error(`No entry in schema for key "${key}"`);
}

export function prismicFieldConfigurationError(
  key?: string,
  expectedComponent?: string,
  actualComponent?: string
) {
  return Error(`Invalid "canvasComponent" found for "${key}":

  Product: "${actualComponent}"
  Prismic: "${expectedComponent}"

Either the form document or the product should be amended so these values align. Consider which interface element should be presented to the user.
`);
}

export function missingCookiesError(missingCookies: string[]) {
  if (env("NEXT_PUBLIC_CANVAS_ENV") === "production") {
    return MISSING_ESSENTIAL_DATA_ERROR;
  }

  return Error(
    `Missing ${missingCookies.join(
      ", "
    )} cookie(s) required to call website APIs. Are you visiting via Website proxy? You may need to visit the root first to have these cookies set.`
  );
}

export type ErrorItem = {
  context: {
    key: string;
    label: string;
  };
  type: string;
};

export const isErrorItem = (err: any): err is ErrorItem => {
  return (
    err.type &&
    err.context?.key !== undefined &&
    err.context?.label !== undefined
  );
};

export const isSchemaValidationErrors = (
  error: unknown
): error is AxiosError & { response: AxiosResponse<ErrorItem[]> } => {
  // ^ Extend AxiosError with a non-optional `response` field
  if (!(error instanceof AxiosError) || !error.response?.data) {
    return false;
  }

  const errorData = error.response.data;

  if (!Array.isArray(errorData)) {
    return false;
  }

  return errorData.every((err) => isErrorItem(err));
};

/**
 * Return only the validation errors relating to the fields in `data`.
 *
 * Used for partial TOC validation.
 */
export function getActiveErrors(
  errorData: ErrorItem[],
  data: { [x: string]: any }
) {
  // Filter out inactive errors by checking if the key exists in `data`
  const activeErrors = errorData.filter((item) => {
    if (
      item.context.label === "characters" ||
      item.context.label === "characters[0].customisations"
    ) {
      return false;
    }
    return _get(data, item.context.label, false) !== false;
  });

  return activeErrors;
}

/**
 * Returns an array of keys for missing entries in the object.
 *
 * Used to output which required values are missing.
 */
export function getMissingKeysFromObject(object: Record<string, any>) {
  const missingKeys = Object.entries(object).reduce((list, [key, value]) => {
    if (_isNil(value)) {
      return [...list, key];
    }
    return list;
  }, [] as string[]);

  if (_isEmpty(missingKeys)) {
    return null;
  }

  return missingKeys;
}

/**
 * Redact values from customisation object for logging to Bugsnag.
 *
 * Just knowing the presence of these personalisations is really helpful for debugging.
 */
// Allow a few special chars to better understand the structure of the value;
// specifically `@`, `.`, `/`, `-`, `:` and whitespace
const ALLOWED_CHARS_REGEX = /[^@./\-:\s]/g;
export function sanitizeCustomisation(customisation: Record<string, any>) {
  return _cloneDeepWith(customisation, (objValue) => {
    if (typeof objValue === "string" || typeof objValue === "number") {
      return objValue.toString().replaceAll(ALLOWED_CHARS_REGEX, "*");
    }
  });
}
